// Libraries
import {
  addMonths,
  addYears,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  endOfYear,
  getWeek,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  setMonth,
  setYear,
  startOfMonth,
  startOfToday,
  startOfWeek,
  startOfYear,
  subMonths,
  subYears,
  isSameMonth,
} from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
// Utils
import { clearTime, isDateInRange } from '@helpers/index';
// Types
import { CalendarView, Days, Months, UseCalendarProps, UseCalendarReturn } from './useCalendar.types';

/**
 * useCalendar hook provides functionality for managing a calendar view and selected dates.
 *
 * @param {UseCalendarProps} options - The options for the useCalendar hook.
 * @param {number[]} options.availableYears - The available years in the calendar.
 * @param {number} options.weekStartsOn - The starting day of the week (0 = Sunday, 1 = Monday, etc.).
 * @param {Date} options.viewing - The initial viewing date of the calendar.
 * @param {Date[]} options.selected - The initially selected dates in the calendar.
 * @param {number} options.numberOfMonths - The number of months to display in the calendar view.
 * @param {Date} options.startDate - The minimum selectable date in the calendar.
 * @param {Date} options.endDate - The maximum selectable date in the calendar.
 * @returns {UseCalendarReturn} - An object containing the calendar state and functions.
 */

export const useCalendar = ({
  availableYears = [new Date().getFullYear()],
  weekStartsOn = Days.SUNDAY,
  viewing: initialViewing = new Date(),
  selected: initialSelected = [],
  numberOfMonths = 1,
  startDate,
  endDate,
}: UseCalendarProps): UseCalendarReturn => {
  const [viewing, setViewing] = useState<Date>(initialViewing);

  /**
   * Sets the viewing date to the current day.
   */
  const viewToday = useCallback(() => setViewing(startOfToday()), [setViewing]);

  /**
   * Sets the viewing date to a specific month.
   * @param {Months} month - The month to view.
   */
  const viewMonth = useCallback((month: Months) => setViewing((v) => setMonth(v, month)), []);

  /**
   * Sets the viewing date to the previous month.
   */
  const viewPreviousMonth = useCallback(() => setViewing((v) => subMonths(v, 1)), []);

  /**
   * Sets the viewing date to the next month.
   */
  const viewNextMonth = useCallback(() => setViewing((v) => addMonths(v, 1)), []);

  /**
   * Sets the viewing date to a specific year.
   * @param {number} year - The year to view.
   */
  const viewYear = useCallback(
    (year: number) =>
      setViewing((v) => {
        const date = setYear(v, year);
        if (startDate) {
          return isBefore(date, startDate) ? startDate : date;
        }
        return date;
      }),
    [startDate]
  );

  /**
   * Sets the viewing date to the previous year.
   */
  const viewPreviousYear = useCallback(() => setViewing((v) => subYears(v, 1)), []);

  /**
   * Sets the viewing date to the next year.
   */
  const viewNextYear = useCallback(() => setViewing((v) => addYears(v, 1)), []);

  const [selected, setSelected] = useState<Date[]>(initialSelected);

  /**
   * Clears the selected dates.
   */
  const clearSelected = () => setSelected([]);

  /**
   * Checks if a date or date range is selected.
   * @param {Date[]} date - The date or date range to check.
   * @returns {boolean} `true` if selected, `false` otherwise.
   */
  const isSelected = useCallback(
    (date: Date[]) => {
      return selected.findIndex((s) => isSameDay(s, date[0])) > -1;
    },
    [selected]
  );

  /**
   * Selects a date or date range.
   * @param {Date[]} date - The date or date range to select.
   * @param {boolean} [replaceExisting=false] - Whether to replace the existing selection.
   */
  const select = useCallback((date: Date[], replaceExisting?: boolean) => {
    if (replaceExisting) {
      setSelected(date);
    } else {
      setSelected((selectedItems) => selectedItems.concat(date));
    }

    setViewing(date[0]);
  }, []);

  /**
   * Deselects a date or date range.
   * @param {Date[]} date - The date or date range to deselect.
   */
  const deselect = useCallback(
    (date: Date[]) =>
      setSelected((selectedItems) =>
        Array.isArray(date)
          ? selectedItems.filter((s) => !date.map((d) => d.getTime()).includes(s.getTime()))
          : selectedItems.filter((s) => !isEqual(s, date))
      ),
    []
  );

  /**
   * Toggles the selection of a date or date range.
   * @param {Date[]} dates - The date or date range to toggle.
   * @param {Function} [onSelected] - Optional callback function for when a selection is made.
   * @param {boolean} [replaceExisting=false] - Whether to replace the existing selection.
   * @returns {void}
   */
  const toggle = useCallback<UseCalendarReturn['toggle']>(
    (dates, onSelected, replaceExisting) => {
      onSelected && onSelected(dates);
      return select(dates, replaceExisting);
    },
    [select]
  );

  /**
   * Selects a range of dates.
   * @param {Date} start - The start date of the range.
   * @param {Date} end - The end date of the range.
   * @param {boolean} [replaceExisting=false] - Whether to replace the existing selection.
   */
  const selectRange = useCallback((start: Date, end: Date, replaceExisting?: boolean) => {
    if (replaceExisting) {
      setSelected(eachDayOfInterval({ start, end }));
    } else {
      setSelected((selectedItems) => selectedItems.concat(eachDayOfInterval({ start, end })));
    }
  }, []);

  /**
   * Deselects a range of dates.
   * @param {Date} start - The start date of the range.
   * @param {Date} end - The end date of the range.
   */
  const deselectRange = useCallback((start: Date, end: Date) => {
    setSelected((selectedItems) =>
      selectedItems.filter(
        (s) =>
          !eachDayOfInterval({ start, end })
            .map((d) => d.getTime())
            .includes(s.getTime())
      )
    );
  }, []);

  /**
   * Generates the current view of the calendar.
   * @returns {CalendarView} The current view of the calendar.
   */
  const generateView = (): CalendarView => {
    const months = eachMonthOfInterval({
      start: startOfMonth(viewing),
      end: endOfMonth(addMonths(viewing, numberOfMonths - 1)),
    }).map((month) => ({
      weeks: eachWeekOfInterval(
        {
          start: startOfMonth(month),
          end: endOfMonth(month),
        },
        { weekStartsOn }
      ).map((week) => ({
        number: getWeek(week, { weekStartsOn }),
        days: eachDayOfInterval({
          start: startOfWeek(week, { weekStartsOn }),
          end: endOfWeek(week, { weekStartsOn }),
        }).map((day) => ({
          date: day,
        })),
      })),
    })) as CalendarView;

    months.forEach((month) => {
      month.weeks.forEach((week) => {
        week.days.forEach((day) => {
          if (!day.date) return;
          if ((startDate && isBefore(day.date, startDate)) || (endDate && isAfter(day.date, endDate))) {
            delete day.date;
          }
        });
      });
    });
    return months;
  };

  const currentView = useMemo<CalendarView>(generateView, [viewing, weekStartsOn, numberOfMonths, endDate, startDate]);

  /**
   * Retrieves the available months based on the viewing date and optional min/max dates.
   * @returns {Date[]} The available months.
   */
  const months = () => {
    const months = eachMonthOfInterval({
      start: startOfYear(viewing),
      end: endOfYear(viewing),
    });

    return months.filter((month) => {
      const isAfterStartDate = startDate ? isAfter(month, startDate) : true;
      const isSameMonthStartDate = startDate ? isSameMonth(month, startDate) : true;

      const isBeforeMaxDate = endDate ? isBefore(month, endDate) : true;
      const isSameMonthMaxDate = endDate ? isSameMonth(month, endDate) : true;

      return (isAfterStartDate || isSameMonthStartDate) && (isBeforeMaxDate || isSameMonthMaxDate);
    });
  };

  /**
   * Retrieves the available years based on the availableYears prop.
   * @returns {Date[]} The available years.
   */
  const getAvailableYears = () => {
    return availableYears.map((year) => {
      return new Date(year, 0, 1);
    });
  };

  return {
    clearTime,
    inRange: isDateInRange,
    setViewing,
    viewToday,
    viewMonth,
    viewPreviousMonth,
    viewNextMonth,
    viewYear,
    viewPreviousYear,
    viewNextYear,
    setSelected,
    clearSelected,
    isSelected,
    select,
    deselect,
    toggle,
    selectRange,
    deselectRange,
    months,
    viewing,
    selected,
    currentView,
    getAvailableYears,
  };
};
